import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { LABEL_COLORS, type Label } from '@/api/types';
import { useAccountContext } from '@/app/accounts';
import { useMenuButtonToggle, useRowMenuTriggers } from '@/components/FolderMenu';
import { Icon } from '@/components/Icon';
import { Menu, type MenuAnchor, type MenuNode } from '@/features/context-menu';
import { useHandheld } from '@/hooks/useMediaQuery';
import { cssVars } from '@/utils/css';
import { labelRouteOf, labelViewPath } from '@/routes/mail/unifiedRoute';
import { LabelDot } from './LabelChips';
import type { LabelDialogAction } from './LabelDialogs';
import { labelFailureCopy } from './labelFailure';
import { LabelDialog } from '@/app/lazyUi';
import { liveLabels } from './keywords';
import { startLabelMigration } from './migrations';
import { LABEL_COLOR_NAMES, useLabelMigration, useLabelMutations, useLabels } from './useLabels';
import './labels-sidebar.css';

/**
 * The sidebar's "Labels" section for ONE mailbox (plan/17 §5).
 *
 * Mounted under that mailbox's folders — once per mailbox in the unified
 * sidebar, never merged, because a label belongs to one account. Rows are the
 * labels shown in the sidebar, in the owner's order; a label hidden from it is
 * still one click away behind "Manage labels", and in every picker.
 *
 * A label being renamed or deleted keeps its row, with the progress of the
 * migrate loop that is rewriting its messages (`migrations.ts`) and a way to
 * start that loop again if a step failed.
 */
export function LabelsSection({ accountId, nested = false }: { accountId: string; nested?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeAccountId, setActiveAccountId } = useAccountContext();
  const handheld = useHandheld();
  const query = useLabels(accountId);
  const { update, reorder } = useLabelMutations(accountId);
  const [dialog, setDialog] = useState<LabelDialogAction | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<{
    label: Label;
    anchor: MenuAnchor;
    returnFocusTo: HTMLElement | null;
  } | null>(null);
  const toggle = useMenuButtonToggle();

  const labels = useMemo(() => query.data ?? [], [query.data]);
  const live = useMemo(() => liveLabels(labels), [labels]);
  const shown = labels.filter((label) => label.state === 'deleting' || label.showInSidebar);
  const hiddenCount = live.filter((label) => !label.showInSidebar).length;
  const selected = labelRouteOf(location.pathname);

  const openLabel = useCallback(
    (label: Label) => {
      // The account first: the label view lists the active mailbox, and the
      // two have to move together (as picking a folder does).
      if (accountId !== activeAccountId) setActiveAccountId(accountId);
      navigate(labelViewPath(accountId, label.id));
    },
    [accountId, activeAccountId, navigate, setActiveAccountId],
  );

  const report = useCallback((cause: unknown) => setStatus(labelFailureCopy(cause, 'That change could not be saved.')), []);

  const closeMenu = useCallback(() => {
    toggle.noteClose();
    setMenuFor(null);
  }, [toggle]);

  const openMenu = useCallback((label: Label, anchor: MenuAnchor, returnFocusTo: HTMLElement | null) => {
    setMenuFor({ label, anchor, returnFocusTo });
  }, []);

  const menuItems = (label: Label): MenuNode[] => {
    if (label.state === 'deleting') return [];
    const order = live;
    const index = order.findIndex((candidate) => candidate.id === label.id);
    const move = (to: number) => {
      const ids = order.map((candidate) => candidate.id);
      ids.splice(index, 1);
      ids.splice(to, 0, label.id);
      setStatus(null);
      reorder.mutate(ids, { onError: report });
    };
    const patch = (next: { color?: Label['color']; showInSidebar?: boolean }) => {
      setStatus(null);
      update.mutate({ label, patch: next }, { onError: report });
    };
    const nodes: MenuNode[] = [];
    if (label.state === 'active') {
      nodes.push({ kind: 'item', id: 'rename', label: 'Rename…', onSelect: () => setDialog({ kind: 'rename', label }) });
    }
    nodes.push({
      kind: 'submenu',
      id: 'colour',
      label: 'Colour',
      items: LABEL_COLORS.map((color) => ({
        kind: 'item' as const,
        id: `colour-${color}`,
        label: color === label.color ? `${LABEL_COLOR_NAMES[color]} (current)` : LABEL_COLOR_NAMES[color],
        onSelect: () => {
          if (color !== label.color) patch({ color });
        },
      })),
    });
    nodes.push({
      kind: 'item',
      id: 'sidebar',
      label: label.showInSidebar ? 'Hide in sidebar' : 'Show in sidebar',
      onSelect: () => patch({ showInSidebar: !label.showInSidebar }),
    });
    if (index > 0 || (index >= 0 && index < order.length - 1)) nodes.push({ kind: 'separator', id: 'sep-order' });
    if (index > 0) nodes.push({ kind: 'item', id: 'move-up', label: 'Move up', onSelect: () => move(index - 1) });
    if (index >= 0 && index < order.length - 1) {
      nodes.push({ kind: 'item', id: 'move-down', label: 'Move down', onSelect: () => move(index + 1) });
    }
    nodes.push(
      { kind: 'separator', id: 'sep-delete' },
      { kind: 'item', id: 'delete', label: 'Delete…', danger: true, onSelect: () => setDialog({ kind: 'delete', label }) },
    );
    return nodes;
  };

  const heading = (
    <div className="labels-section__head">
      <span className="labels-section__title section-title">Labels</span>
      <button
        type="button"
        className="labels-section__add"
        aria-label="Create label"
        title="Create label"
        onClick={() => setDialog({ kind: 'create' })}
      >
        <Icon name="plus" size={14} />
      </button>
    </div>
  );

  return (
    <div className="labels-section">
      {heading}
      {query.isError && (
        <div className="folder-tree__status" role="alert">
          <p className="muted">Labels couldn’t be loaded.</p>
          <button type="button" className="btn btn--ghost" onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      )}
      {query.isSuccess && live.length === 0 && <p className="muted labels-section__empty">No labels yet</p>}
      {/* A list, not more tree: the rows go to places but have no children,
          and inside a mailbox's treeitem a second group would read as more
          of its folders. The open label is `aria-current`. */}
      <ul className="folder-tree labels-section__rows" aria-label="Labels">
        {shown.map((label) => (
          <LabelRow
            key={label.id}
            accountId={accountId}
            label={label}
            depth={nested ? 1 : 0}
            selected={selected?.accountId === accountId && selected.labelId === label.id}
            menuOpen={menuFor?.label.id === label.id}
            hasMenu={label.state !== 'deleting'}
            onOpen={() => openLabel(label)}
            onMenu={openMenu}
            toggle={toggle}
          />
        ))}
      </ul>
      {live.length > 0 && (
        <button type="button" className="btn btn--ghost labels-section__manage" onClick={() => setDialog({ kind: 'manage' })}>
          {hiddenCount > 0 ? `Manage labels (${hiddenCount} hidden)` : 'Manage labels'}
        </button>
      )}
      {status !== null && (
        <p className="muted folder-tree__status" role="status">
          {status}
        </p>
      )}
      {menuFor !== null && (
        <Menu
          label={`Label actions for ${menuFor.label.name}`}
          items={menuItems(menuFor.label)}
          anchor={menuFor.anchor}
          returnFocusTo={menuFor.returnFocusTo}
          onClose={closeMenu}
          variant={handheld ? 'sheet' : 'popover'}
        />
      )}
      {dialog !== null && (
        <LabelDialog
          accountId={accountId}
          action={dialog}
          labels={labels}
          onClose={() => setDialog(null)}
          onOpenLabel={openLabel}
        />
      )}
    </div>
  );
}

function LabelRow({
  accountId,
  label,
  depth,
  selected,
  menuOpen,
  hasMenu,
  onOpen,
  onMenu,
  toggle,
}: {
  accountId: string;
  label: Label;
  depth: number;
  selected: boolean;
  menuOpen: boolean;
  hasMenu: boolean;
  onOpen: () => void;
  onMenu: (label: Label, anchor: MenuAnchor, returnFocusTo: HTMLElement | null) => void;
  toggle: ReturnType<typeof useMenuButtonToggle>;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const migration = useLabelMigration(label.id);
  const rowRef = useRef<HTMLLIElement>(null);
  useRowMenuTriggers(
    rowRef,
    hasMenu,
    useCallback(
      (anchor: MenuAnchor, returnFocusTo: HTMLElement | null) => onMenu(label, anchor, returnFocusTo),
      [label, onMenu],
    ),
  );

  const busy = label.state !== 'active';
  const deleting = label.state === 'deleting';
  const count = migration?.migrated ?? label.migratedCount;
  const progress = busy
    ? `${deleting ? 'Removing…' : 'Renaming…'}${count > 0 ? ` ${count.toLocaleString()} ${count === 1 ? 'message' : 'messages'}` : ''}`
    : undefined;

  return (
    <li ref={rowRef} className="labels-section__item" aria-busy={busy}>
      <div
        className={'folder-tree__row labels-section__row' + (selected ? ' is-active' : '') + (menuOpen ? ' has-menu' : '')}
        style={cssVars({ '--depth': depth })}
      >
        <span className="folder-tree__twisty folder-tree__twisty--placeholder" />
        <button
          type="button"
          className="folder-tree__main"
          onClick={onOpen}
          disabled={deleting}
          {...(selected ? { 'aria-current': 'page' as const } : {})}
        >
          <LabelDot label={label} />
          <span className="folder-tree__label">{label.name}</span>
        </button>
        {progress !== undefined && (
          <span className="folder-tree__busy" role="status">
            {migration?.error !== undefined ? 'Stopped' : progress}
          </span>
        )}
        {migration?.error !== undefined && (
          <button
            type="button"
            className="btn btn--ghost labels-section__retry"
            title={migration.error}
            aria-label={`Try again: ${deleting ? 'removing' : 'renaming'} ${label.name}`}
            onClick={() => void startLabelMigration(queryClient, api, accountId, label)}
          >
            Try again
          </button>
        )}
        {hasMenu && (
          <button
            type="button"
            className="folder-tree__menu-button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Label actions for ${label.name}`}
            onPointerDown={toggle.onPointerDown}
            onClick={(event) => {
              if (toggle.consumeClick()) return;
              const button = event.currentTarget;
              onMenu(label, button.getBoundingClientRect(), button);
            }}
          >
            <Icon name="more" size={14} />
          </button>
        )}
      </div>
    </li>
  );
}
